import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { FaCalendarAlt } from "react-icons/fa";
import Layout from "../elements/Layout";
import { getAuditLogs } from "../utils/api/api";
import Loading from "../elements/Loading";

const AuditLogs = () => {
    const [logs, setLogs] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(2000);
    const [totalPages, setTotalPages] = useState(1);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState([
        {
            startDate: new Date("2024-01-01"),
            endDate: new Date("2025-12-31"),
            key: "selection",
        },
    ]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const response = await getAuditLogs({
                page,
                pageSize,
                start_date: format(dateRange[0].startDate, "yyyy-MM-dd"),
                end_date: format(dateRange[0].endDate, "yyyy-MM-dd"),
            });

            setLogs(response.audits || []);
            setTotalPages(response.totalPages || 1);
        } catch (error) {
            console.error("Error fetching audit logs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [page, pageSize, dateRange]);

    return (
        <Layout text="Audit Trail">
            {loading && <Loading />}
            <div className="p-6">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6">
                    <h2 className="text-2xl font-semibold">Audit Logs</h2>

                    <div className="relative">
                        <button
                            onClick={() => setShowDatePicker(!showDatePicker)}
                            className="flex items-center space-x-2 px-4 py-2 border rounded-md shadow-md bg-white text-gray-700"
                        >
                            <FaCalendarAlt className="text-indigo-600" />
                            <span>
                                {format(dateRange[0].startDate, "MMM dd, yyyy")} -{" "}
                                {format(dateRange[0].endDate, "MMM dd, yyyy")}
                            </span>
                        </button>

                        {showDatePicker && (
                            <div className="absolute top-12 right-0 z-10 shadow-lg bg-white p-2 border rounded-lg">
                                <DateRange
                                    ranges={dateRange}
                                    onChange={(ranges) => setDateRange([ranges.selection])}
                                    moveRangeOnFirstSelection={false}
                                />
                            </div>
                        )}
                    </div>
                </div>

                <div className="space-y-4">
                    {logs.length > 0 ? (
                        logs.map((log) => (
                            <div key={log.id} className="border p-4 rounded-lg shadow-md">
                                <div>
                                    <h3 className="text-lg font-medium">{log.action}</h3>
                                    <p className="text-sm text-gray-500">
                                        {log.description || "No additional details."}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        By {log?.user?.first_name} {log?.user?.last_name} •{" "}
                                        {format(new Date(log.createdAt), "PPpp")}
                                    </p>
                                </div>
                            </div>
                        ))
                    ) : (
                        <p className="text-gray-500">No logs found.</p>
                    )}
                </div>

                <div className="mt-6 flex justify-center space-x-4">
                    <button
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                        className={`px-4 py-2 border rounded-md ${page === 1 ? "bg-gray-200 text-gray-500" : "bg-indigo-600 text-white"
                            }`}
                    >
                        Previous
                    </button>
                    <span className="text-gray-700">
                        Page {page} of {totalPages}
                    </span>
                    <button
                        disabled={page === totalPages}
                        onClick={() => setPage(page + 1)}
                        className={`px-4 py-2 border rounded-md ${page === totalPages ? "bg-gray-200 text-gray-500" : "bg-indigo-600 text-white"
                            }`}
                    >
                        Next
                    </button>
                </div>
            </div>
        </Layout>
    );
};

export default AuditLogs;
